import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Autocomplete,
	Checkbox,
	DialogContent,
	FormControlLabel,
	Grid,
	TextField,
	Typography,
} from "@mui/material";
import { ContactMail } from "@mui/icons-material";

import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { ContactBlock } from "../../util/Batch";
import { MYSQL_LIMITS } from "../../util/constants";
import { canCreate } from '../../util/access';
import ClientCreateDialog, { CreatedClient } from "./ClientCreateDialog";
import {
	SavedAddress,
	autoFillFrom,
	contactFromSaved,
	emptyContact,
	normalizeContact,
	savedAddressLabel,
} from "./contactBlock";

/** Walk-in: its book is shared by everyone, so saved entries are not offered. */
const WALK_IN_CLIENT = 1;

interface Props {
	open: boolean;
	onClose: () => void;
	/** Client the document is for, or -1 / 1 when there is none worth a book. */
	clientId?: number;
	value?: ContactBlock | null;
	onApply: (contact: ContactBlock | null) => void;
	/** Passed through to the create dialog so a duplicate name is caught early. */
	existingNames?: readonly string[];
	onClientCreated?: (client: CreatedClient) => void;
}

const AddressLineDialog = ({
	open,
	onClose,
	clientId,
	value,
	onApply,
	existingNames = [],
	onClientCreated,
}: Props) => {
	const { api, access } = useAuth();
	const { t, queueReRun } = useUI();
	const themeColor = useMoleThemeColor();

	const valueRef = useRef(value);
	valueRef.current = value;

	const [form, setForm] = useState<ContactBlock>(emptyContact());
	const [saved, setSaved] = useState<SavedAddress[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [saveToBook, setSaveToBook] = useState(true);
	const [makeDefault, setMakeDefault] = useState(false);
	const [createOpen, setCreateOpen] = useState(false);

	const hasClient = Number(clientId) > 0;
	const booked = hasClient && Number(clientId) !== WALK_IN_CLIENT;

	// Read through refs so this keeps a stable identity: it sits in an effect's
	// dependencies, and a new function each render would refetch in a loop.
	const apiRef = useRef(api);
	apiRef.current = api;
	const queueReRunRef = useRef(queueReRun);
	queueReRunRef.current = queueReRun;

	const fetchSaved = useCallback(async () => {
		if (!booked) {
			setSaved([]);
			return;
		}
		setIsLoading(true);
		const res = await apiRef.current.get(`/clients/${clientId}/addresses`);
		setIsLoading(false);
		if (!res) return;
		if (res.status === 200) {
			setSaved(Array.isArray(res.data) ? res.data : []);
		} else if (res.status === 401 || res.status === 422) {
			queueReRunRef.current(fetchSaved);
		}
	}, [booked, clientId]);

	// Seeded when the dialog opens, not on every change of the parent's object.
	useEffect(() => {
		if (!open) return;
		setForm({ ...emptyContact(), ...(valueRef.current || {}) });
		setSaveToBook(true);
		setMakeDefault(false);
		fetchSaved();
	}, [open, fetchSaved]);

	useEffect(() => {
		if (!open) return;
		setForm((prev) => {
			const fill = autoFillFrom(saved, prev);
			return fill ? { ...emptyContact(), ...contactFromSaved(fill) } : prev;
		});
	}, [open, saved]);

	const selectedSaved = useMemo(
		() => saved.find((entry) => entry.id === form.address_id) || null,
		[saved, form.address_id],
	);

	const patch = (field: keyof ContactBlock, text: string) =>
		setForm((prev) => ({ ...prev, [field]: text }));

	const handleApply = () => {
		const normalized = normalizeContact(form);
		onApply(normalized && { ...normalized, save_to_book: saveToBook, make_default: makeDefault });
		onClose();
	};

	const handleClear = () => {
		setForm(emptyContact());
		onApply(null);
		onClose();
	};

	const field = (
		name: keyof ContactBlock,
		labelKey: string,
		maxLength: number,
		extra?: { multiline?: boolean; rows?: number; size?: number },
	) => (
		<Grid size={{ xs: 12, sm: extra?.size ?? 6 }}>
			<TextField
				fullWidth
				size="small"
				variant="filled"
				label={t(labelKey)}
				value={String(form[name] ?? "")}
				inputProps={{ maxLength }}
				multiline={extra?.multiline}
				rows={extra?.rows}
				onChange={(e) => patch(name, e.target.value)}
			/>
		</Grid>
	);

	return (
		<>
			<MoleDialog open={open} onClose={onClose} maxWidth="sm" fullWidth themeColor={themeColor}>
				<MoleDialogHeader
					title={t("common:addressLine.title")}
					icon={<ContactMail />}
					onClose={onClose}
					themeColor={themeColor}
				/>
				<DialogContent sx={{ pt: 2 }}>
					<Grid container spacing={2}>
						<Grid size={{ xs: 12 }}>
							<Autocomplete
								options={saved}
								loading={isLoading}
								disabled={!booked}
								value={selectedSaved}
								getOptionLabel={(entry) => savedAddressLabel(entry)}
								isOptionEqualToValue={(option, selected) => option.id === selected?.id}
								clearOnBlur
								autoSelect={false}
								autoComplete={false}
								onChange={(_, entry, reason) => {
									if (reason === "selectOption" && entry) {
										setForm({ ...emptyContact(), ...contactFromSaved(entry) });
									} else if (reason === "clear") {
										setForm((prev) => ({ ...prev, address_id: null }));
									}
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										size="small"
										variant="filled"
										label={t("common:addressLine.saved")}
										helperText={
											!hasClient
												? t("common:addressLine.noClient")
												: !booked
													? t("common:addressLine.walkIn")
													: saved.length === 0
														? t("common:addressLine.savedEmpty")
														: ""
										}
									/>
								)}
							/>
						</Grid>

						{field("label", "common:addressLine.label", MYSQL_LIMITS.VARCHAR(50).maxLength)}
						{field("con_name", "common:addressLine.name", MYSQL_LIMITS.VARCHAR(100).maxLength)}
						{field("con_phone", "common:addressLine.phone", MYSQL_LIMITS.VARCHAR(50).maxLength)}
						{field("con_email", "common:addressLine.email", MYSQL_LIMITS.VARCHAR(100).maxLength)}
						{field("con_address", "common:addressLine.address", MYSQL_LIMITS.VARCHAR(255).maxLength, {
							multiline: true,
							rows: 2,
							size: 12,
						})}
						{field("con_details", "common:addressLine.details", MYSQL_LIMITS.TEXT.maxLength, {
							multiline: true,
							rows: 2,
							size: 12,
						})}

						{String(form.con_name ?? "").trim() && (
							<Grid size={{ xs: 12 }}>
								<Typography variant="caption" color="text.secondary">
									{t("common:addressLine.replacesName")}
								</Typography>
							</Grid>
						)}

						{booked && (
							<Grid size={{ xs: 12 }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={saveToBook}
											onChange={(e) => {
												setSaveToBook(e.target.checked);
												if (!e.target.checked) setMakeDefault(false);
											}}
										/>
									}
									label={t("common:addressLine.saveToBook")}
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={makeDefault}
											disabled={!saveToBook}
											onChange={(e) => setMakeDefault(e.target.checked)}
										/>
									}
									label={t("common:addressLine.makeDefault")}
								/>
							</Grid>
						)}
					</Grid>
				</DialogContent>
				<MoleDialogActions
					leading={
						!hasClient && canCreate(access, "client") ? (
							<MoleCancelButton onClick={() => setCreateOpen(true)}>
								{t("common:addressLine.createClient")}
							</MoleCancelButton>
						) : undefined
					}
				>
					<MoleCancelButton onClick={handleClear}>{t("common:addressLine.clear")}</MoleCancelButton>
					<MoleCancelButton onClick={onClose}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
					<MolePrimaryButton onClick={handleApply}>
						{t("common:ui.buttons.apply")}
					</MolePrimaryButton>
				</MoleDialogActions>
			</MoleDialog>

			<ClientCreateDialog
				open={createOpen}
				onClose={() => setCreateOpen(false)}
				prefill={form}
				existingNames={existingNames}
				onClientCreated={(client) => {
					onClientCreated?.(client);
					setCreateOpen(false);
				}}
			/>
		</>
	);
};

export default AddressLineDialog;
